import React from 'react'
import { useNavigate } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import { InvoiceMasterData } from '../../../apis/models/InvoiceMasterRequest';
import { TaxResponseData } from '../../../apis/models/TaxResponse';
import styles from './InvoiceHeader.module.scss'
import DateWithoutTime from '../../../libs/dateWithoutTime';
import { INVOICE_TAB_INDEX, InvStatus, StatusOptions } from '../../../libs/constants';
import { formatCurrency, fromValToName } from '../../../libs/helpers';
import StatusHistoryDialog from '../../dialogs/invoiceDetails/StatusHistoryDialog';
import { getInvStatusHistory, getShippingFee } from '../../../apis/invoice-api';
import { StatusHistoryResponseData } from '../../../apis/models/StatusHistoryResponse';
import ProcessFeeMasterDialog from '../../dialogs/masters/ProcessFeeMasterDialog';
import OfficeWorkFeeMasterDialog from '../../dialogs/masters/OfficeWorkFeeMasterDialog/OfficeWorkFeeMasterDialog';
import WrappingSizeMasterDialog from '../../dialogs/masters/WrappingSizeMasterDialog/WrappingSizeMasterDialog';
import DeliveryMethodMasterDialog from '../../dialogs/masters/DeliveryMethodMasterDialog/DeliveryMethodMasterDialog';
import InvoiceCommitDialog from '../../dialogs/confirmDialogs/InvoiceCommitDialog';
import InvoiceSaveAndCommitDialog from '../../dialogs/confirmDialogs/InvoiceSaveAndCommitDialog';
import InvoiceRevertDialog from '../../dialogs/confirmDialogs/InvoiceRevertDialog';
import ResetDialog from '../../dialogs/confirmDialogs/ResetDialog';
import { ShippingFeeResponseData } from '../../../apis/models/ShippingFeeResponseData';
import { Alert, Snackbar } from '@mui/material';
import InvoiceDownloadDialog from '../../dialogs/InvoiceDownloadDialog';
import { callDownloadInvoiceApi } from '../../../apis/invmaster-api';
import { InvoiceContentTabType } from '../InvoiceContent/InvoiceContent';

type ConfirmDialogType = {
  type: "commit" | "saveCommit" | "revert" | "reset";
  option?: any;
}

const InvoiceHeader = (props: InvoiceHeaderProps) => {
  const navigate = useNavigate();
  const [statusEl, setStatusEl] = React.useState<HTMLButtonElement | null>(null);
  const [masterMenuEl, setMasterMenuEl] = React.useState<HTMLElement | null>(null);
  const [openStatusHistory, setOpenStatusHistory] = React.useState<boolean>(false);
  const [statusHistory, setStatusHistory] = React.useState<StatusHistoryResponseData[]>([]);
  const [openMasterDialog, setOpenMasterDialog] = React.useState<"process" | "office" | "wrapping" | "delivery" | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState<ConfirmDialogType | null>(null);
  const [openDownloadDialog, setOpenDownloadDialog] = React.useState<boolean>(false);
  const [shippingError, setShippingError] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!props.invoiceMaster || props.invoiceMaster.status !== InvStatus.NOT_CONFIRMED) {
      return (() => {})
    }

    let isActive = true;
    getShippingFee(props.invoiceMaster?.invoiceMasterId)
      .then(response => {
        if (!isActive) {
          return;
        }
        const shippingData = response.data.data as ShippingFeeResponseData[];
        setShippingError(shippingData.some(data => data.actualShippingFee <= 0));
    })

    return (() => {
      isActive = false;
    })

  }, [props.invoiceMaster]);  

  const invoiceData = React.useMemo(() => {
    const invoiceMasterId = props.invoiceMaster?.invoiceMasterId ?? "";
    const brandName = props.invoiceMaster?.brand?.brandName ?? "";
    const brandNo = props.invoiceMaster?.brandNo ?? "";
    let totalPrice = props.invoiceDetails?.reduce((acc, item) => acc + item.totalPrice, 0) ?? 0;
    totalPrice = Math.floor(totalPrice + totalPrice * ((props.tax?.taxPercent ?? 10) / 100));

    const invoiceStatus = props.invoiceMaster?.status;
    const invoiceStatusLabel = fromValToName(StatusOptions, props.invoiceMaster?.status);
    const from = new DateWithoutTime(props.invoiceMaster?.from ?? null);
    const to = new DateWithoutTime(props.invoiceMaster?.to ?? null);
    const invoicePeriodLabel = `${from.getValue4Display()} ～ ${to.getValue4Display()}`;
    
    return {invoiceMasterId, brandName, brandNo, totalPrice, invoiceStatus, invoiceStatusLabel, invoicePeriodLabel}
  }, [props.invoiceMaster, props.tax, props.invoiceDetails]);

  const openHistoryDialog = async () => {
    const response = await getInvStatusHistory(invoiceData.invoiceMasterId);
    setStatusHistory(response.data.data);
    setOpenStatusHistory(true);
  }

  const onSuccessMasterFeeMaster = () => {
    setOpenMasterDialog(null);
    props.onMasterChange();
  }

  const onClickTab = (tabIndex: INVOICE_TAB_INDEX) => {
    if (props.selectedTab === tabIndex) {
      return;
    }
    if (props.needSave) {
      setOpenConfirmDialog({type: "reset", option: tabIndex})
    } else {
      moveTab(tabIndex);
    }
  }

  const moveTab = (tabIndex: INVOICE_TAB_INDEX) => {
    if (tabIndex === INVOICE_TAB_INDEX.PROCESS) {
      navigate(`/invoice/${invoiceData.invoiceMasterId}/process`)
    } else if (tabIndex === INVOICE_TAB_INDEX.OFFICE) {
      navigate(`/invoice/${invoiceData.invoiceMasterId}/office`)
    } else if (tabIndex === INVOICE_TAB_INDEX.WRAPPING) {
      navigate(`/invoice/${invoiceData.invoiceMasterId}/wrapping`)
    } else if (tabIndex === INVOICE_TAB_INDEX.LOGISTIC) {
      navigate(`/invoice/${invoiceData.invoiceMasterId}/logistic`)
    } else if (tabIndex === INVOICE_TAB_INDEX.SHIPPING) {
      navigate(`/invoice/${invoiceData.invoiceMasterId}/shipping`)
    } else if (tabIndex === INVOICE_TAB_INDEX.ARRIVAL) {
      navigate(`/invoice/${invoiceData.invoiceMasterId}/arrival`)
    } else {
      navigate(`/invoice/${invoiceData.invoiceMasterId}`)
    }
  }

  const onChangeInvoiceStatus = (status: InvStatus) => {
    if (status === InvStatus.CONFIRMED) {
      if (props.needSave) {
        setOpenConfirmDialog({type: "saveCommit"});
      } else {
        setOpenConfirmDialog({type: "commit"});
      }
    } else if (status === InvStatus.NOT_CONFIRMED) {
      setOpenConfirmDialog({type: "revert"});
    }
  }

  const saveAndCommit = () => {
    setOpenConfirmDialog(null);
    props.onSaveAndCommit();
  }

  const changeStatus = (status: InvStatus) => {
    setOpenConfirmDialog(null);
    props.onChangeStatus(status);
  }

  const confirmDialogElement = () => {
    if (!openConfirmDialog) {
      return <></>;
    }
    if (openConfirmDialog.type === "commit") {
      return (
        <InvoiceCommitDialog
          open
          onClose={() => setOpenConfirmDialog(null)}
          onOk={() => changeStatus(InvStatus.CONFIRMED)}
          brandName={invoiceData.brandName}
          invoicePeriod={invoiceData.invoicePeriodLabel}
        />
      )
    } else if (openConfirmDialog.type === "saveCommit") {
      return (
        <InvoiceSaveAndCommitDialog
          open
          onClose={() => setOpenConfirmDialog(null)}
          onOk={() => saveAndCommit()}
        />
      )
    } else if (openConfirmDialog.type === "revert") {
      return (
        <InvoiceRevertDialog
          open
          isEditMode={false}
          onClose={() => setOpenConfirmDialog(null)}
          onOk={() => changeStatus(InvStatus.NOT_CONFIRMED)}
        />
      )
    } else if (openConfirmDialog.type === "reset") {
      return (
        <ResetDialog
          open
          onClose={() => setOpenConfirmDialog(null)}  
          onOk={() => moveTab(openConfirmDialog.option)}
        />
      )
    }
  }

  const onClickStatusButton = (element: HTMLButtonElement) => {
    if (props.error || shippingError) {
      return;
    }
    setStatusEl(element);
  }

  const downloadInvoice = async (pdf: boolean, excel: boolean) => {
    setOpenDownloadDialog(false)

    if (!props.invoiceMaster) {
      return;
    }

    const response = await callDownloadInvoiceApi(props.invoiceMaster.invoiceMasterId, pdf, excel)
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `請求書_ブランド_${props.invoiceMaster.brandNo}_${props.invoiceMaster.year}${String(props.invoiceMaster.month).padStart(2, '0')}.zip`);
    document.body.appendChild(link);
    link.click();

    props.onChangeStatus(InvStatus.EXPORT);
  }

  return (
    <div style={{backgroundColor: "#F2F2F2"}}>
      <Snackbar
        anchorOrigin={{vertical: "top", horizontal: "center"}}
        open={shippingError}
      >
        <Alert severity={"error"}>運賃に未入力の項目があります</Alert>
      </Snackbar>
      <Stack
        className={styles.brandInfo}
        direction="row"
        alignItems="center"
      >
        <div style={{height: "34px"}}>
          <h2>{invoiceData.brandName}</h2>
          <h5>{"（NO." + invoiceData.brandNo + "）"}</h5>
          {invoiceData.invoiceStatus === InvStatus.NOT_CONFIRMED ? (
            <img
              alt="setting_icon"
              title={`${invoiceData.brandName}＿作業内容管理`}
              src="/icons/settings_dark.svg"
              onClick={(e) => setMasterMenuEl(e.currentTarget)}
            />
          ) : (
            <img
              alt="setting_icon"
              className={styles.disabled}
              src="/icons/settings.svg"
            />
          )}
        </div>
        {invoiceData.invoiceStatus === InvStatus.CONFIRMED || invoiceData.invoiceStatus === InvStatus.EXPORT ? (
          <Button
            className={styles.download_btn}
            size="medium"
            variant="contained"
            color="secondary"
            disableElevation
            onClick={() => setOpenDownloadDialog(true)}
          >
            請求書ダウンロード
          </Button>
        ) : (
          <></>
        )}
      </Stack>
      <div className={styles.subheader_container}>
        <div className={styles.details_info}>請求金額</div>
        <div className={styles.details_info}>請求ステータス</div>
        <div className={styles.details_info}>請求期間</div>
        <div className={`${styles.col} ${styles.total_price}`}>
          {formatCurrency(invoiceData.totalPrice)}
          <span>(税込)</span>
        </div>
        <div className={`${styles.col} ${styles.invoice_status}`}>
          <button
            className={styles.status_dropdown}
            onClick={(e) => onClickStatusButton(e.currentTarget)}
            title={props.error ? "エラー状態ではステータスの変更は出来ません" : shippingError ? "運賃に未入力の項目があります" : ""}
          >
            <span>
              {invoiceData.invoiceStatus === InvStatus.NOT_CONFIRMED ? (
                <img alt="pending_icon" src="/icons/pending_inv.svg" />
              ) : invoiceData.invoiceStatus === InvStatus.CONFIRMED ? (
                <img alt="confirmed_icon" src="/icons/confirmed_inv.svg" />
              ) : (
                <img alt="exported_icon" src="/icons/exported_inv.svg" />
              )}
              {invoiceData.invoiceStatusLabel}
            </span>
            <ArrowDropDownIcon color="primary" />
          </button>
          <div
            className={styles.changelog}
            onClick={() => openHistoryDialog()}
          >
            変更履歴
          </div>
          <Popper open={statusEl !== null} anchorEl={statusEl}>
            <ClickAwayListener onClickAway={() => setStatusEl(null)}>
              {invoiceData.invoiceStatus !== InvStatus.NOT_CONFIRMED ? (
                <div
                  className={styles.status_dropdown_option}
                  onClick={() => onChangeInvoiceStatus(InvStatus.NOT_CONFIRMED)}
                >
                  確定取り消し
                </div>
              ) : (
                <div
                  className={styles.status_dropdown_option}
                  onClick={() => onChangeInvoiceStatus(InvStatus.CONFIRMED)}
                >
                  請求確定
                </div>
              )}
            </ClickAwayListener>
          </Popper>
        </div>
        <div className={`${styles.col} ${styles.period}`}>
          {invoiceData.invoicePeriodLabel}
        </div>
      </div>
      <div className={styles.tabs__header}>
        <div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.CONTENT ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.CONTENT)}
          >
            請求内容
          </div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.PROCESS ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.PROCESS)}
          >
            加工費
          </div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.OFFICE ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.OFFICE)}
          >
            事務費用
          </div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.WRAPPING ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.WRAPPING)}
          >
            資材費
          </div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.LOGISTIC ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.LOGISTIC)}
          >
            物流費
          </div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.SHIPPING ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.SHIPPING)}
          >
            {shippingError && <div className={styles.errorMark}></div>}
            運賃
          </div>
          <div
            className={props.selectedTab === INVOICE_TAB_INDEX.ARRIVAL ? `${styles.selected_tab}` : ''}
            onClick={() => onClickTab(INVOICE_TAB_INDEX.ARRIVAL)}
          >
            入荷情報
          </div>
        </div>
      </div>

      <Popper open={masterMenuEl !== null} anchorEl={masterMenuEl} placement="bottom-start">
          <ClickAwayListener onClickAway={() => setMasterMenuEl(null)}>
            <div className={styles.menu}>
              <div onClick={() => {setOpenMasterDialog("process"); setMasterMenuEl(null)}}>加工作業項目</div>
              <div onClick={() => {setOpenMasterDialog("office"); setMasterMenuEl(null)}}>事務作業項目</div>
              <div onClick={() => {setOpenMasterDialog("wrapping"); setMasterMenuEl(null)}}>資材サイズ</div>
              <div onClick={() => {setOpenMasterDialog("delivery"); setMasterMenuEl(null)}}>発送方法</div>
            </div>
          </ClickAwayListener>
      </Popper>
      {openStatusHistory ? 
        <StatusHistoryDialog open={openStatusHistory} onClose={() => setOpenStatusHistory(false)} data={statusHistory}/>
      : 
        <></>
      }
      {confirmDialogElement()}
      {openMasterDialog === "process" &&
        <ProcessFeeMasterDialog
          open
          onCancel={() => setOpenMasterDialog(null)}
          onClose={() => setOpenMasterDialog(null)}
          onSuccess={() => onSuccessMasterFeeMaster()}
          brandNo={invoiceData.brandNo}
          invoiceMasterId={invoiceData.invoiceMasterId}
        />
      }
      {openMasterDialog === "office" &&
        <OfficeWorkFeeMasterDialog
          open
          onCancel={() => setOpenMasterDialog(null)}
          onClose={() => setOpenMasterDialog(null)}
          onSuccess={() => onSuccessMasterFeeMaster()}
          brandNo={invoiceData.brandNo}
          invoiceMasterId={invoiceData.invoiceMasterId}
        />
      }
      {openMasterDialog === "wrapping" &&
        <WrappingSizeMasterDialog
          open
          onCancel={() => setOpenMasterDialog(null)}
          onClose={() => setOpenMasterDialog(null)}
          onSuccess={() => onSuccessMasterFeeMaster()}
          brandNo={invoiceData.brandNo}
          invoiceMasterId={invoiceData.invoiceMasterId}
        />
      }
      {openMasterDialog === "delivery" &&
        <DeliveryMethodMasterDialog
          open
          onCancel={() => setOpenMasterDialog(null)}
          onClose={() => setOpenMasterDialog(null)}
          onSuccess={() => onSuccessMasterFeeMaster()}
          brandNo={invoiceData.brandNo}
          invoiceMasterId={invoiceData.invoiceMasterId}
        />
      }
      {(openDownloadDialog && props.invoiceMaster) &&
        <InvoiceDownloadDialog
          targetInvoice={props.invoiceMaster}
          open={true}
          onClose={() => setOpenDownloadDialog(false)}
          onDownload={(invoice, pdf, excel) => downloadInvoice(pdf, excel)}
        />
      }
    </div>
  );
};

export default InvoiceHeader;

export type InvoiceHeaderProps = {
  invoiceMaster?: InvoiceMasterData;
  invoiceDetails?: InvoiceContentTabType[];
  tax?: TaxResponseData;
  selectedTab: INVOICE_TAB_INDEX;
  needSave: boolean;
  error: boolean;
  onSaveAndCommit: () => void;
  onMasterChange: () => void;
  onChangeStatus: (status: InvStatus) => void;
};
